import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, Check } from "@mui/icons-material";
import type { CompanyPodcastOption } from "./metaConnectUtils";
import { inviteTextSx } from "./inviteTypography";
import { ensureDirectS3Url } from "../../utils/imageUtils";

export interface InviteMetaPodcastSubtitleProps {
  inviterName: string;
  targetPodcastId?: string;
  targetPodcastTitle?: string;
  podcasts: CompanyPodcastOption[];
  selectedPodcastIds: string[];
  onSelectedPodcastIdsChange?: (ids: string[]) => void;
  interactive?: boolean;
}

function resolvePodcastImageSrc(image?: string): string | undefined {
  if (!image) return undefined;
  const resolved = ensureDirectS3Url(image);
  return resolved || undefined;
}

function PodAvatarInline({
  podcast,
  size = 20,
}: {
  podcast: CompanyPodcastOption;
  size?: number;
}) {
  const src = resolvePodcastImageSrc(podcast.image);
  return (
    <Avatar
      src={src}
      alt={podcast.title}
      sx={{
        width: size,
        height: size,
        fontSize: "0.65rem",
        flexShrink: 0,
        display: "inline-flex",
        bgcolor: "common.black",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {!src ? podcast.title.trim().charAt(0).toUpperCase() : undefined}
    </Avatar>
  );
}

function separatorBefore(index: number, total: number): string | null {
  if (index === 0) return null;
  if (index === total - 1) return " y ";
  return ", ";
}

function PodcastListInPill({
  podcasts: items,
}: {
  podcasts: CompanyPodcastOption[];
}) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        justifyContent: "center",
        columnGap: 0.25,
        rowGap: 0.5,
      }}
    >
      {items.map((podcast, index) => {
        const sep = separatorBefore(index, items.length);
        return (
          <Box
            key={podcast.podcastId}
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {sep ? (
              <Box component="span" sx={{ mx: 0.25, fontWeight: 500 }}>
                {sep}
              </Box>
            ) : null}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                lineHeight: 1,
              }}
            >
              <PodAvatarInline podcast={podcast} />
              <Box component="span">{podcast.title}</Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export const InviteMetaPodcastSubtitle: React.FC<InviteMetaPodcastSubtitleProps> = ({
  inviterName,
  targetPodcastId,
  targetPodcastTitle,
  podcasts,
  selectedPodcastIds,
  onSelectedPodcastIdsChange,
  interactive = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const selectedPodcasts = useMemo(() => {
    return selectedPodcastIds
      .map((id) => podcasts.find((p) => p.podcastId === id))
      .filter((p): p is CompanyPodcastOption => !!p);
  }, [selectedPodcastIds, podcasts]);

  const togglePodcast = (podcastId: string) => {
    if (!targetPodcastId || !onSelectedPodcastIdsChange) return;
    if (podcastId === targetPodcastId) return;

    if (selectedPodcastIds.includes(podcastId)) {
      onSelectedPodcastIdsChange(
        selectedPodcastIds.filter((id) => id !== podcastId),
      );
    } else {
      onSelectedPodcastIdsChange([...selectedPodcastIds, podcastId]);
    }
  };

  const canOpenMenu = interactive && podcasts.length > 0;

  const pillPodcasts: CompanyPodcastOption[] =
    selectedPodcasts.length > 0
      ? selectedPodcasts
      : targetPodcastTitle
        ? [
            {
              podcastId: targetPodcastId ?? "",
              title: targetPodcastTitle,
              hasMetaConnection: false,
            },
          ]
        : [{ podcastId: "", title: "este podcast", hasMetaConnection: false }];

  return (
    <Typography
      variant="body1"
      align="center"
      component="div"
      sx={{
        ...inviteTextSx,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: 0.75,
        lineHeight: 1.5,
      }}
    >
      <Box component="span">{inviterName} te ha invitado al podcast</Box>

      <Box
        component={canOpenMenu ? "button" : "span"}
        type={canOpenMenu ? "button" : undefined}
        onClick={
          canOpenMenu
            ? (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)
            : undefined
        }
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          m: 0,
          px: 1.25,
          py: 0.625,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 999,
          bgcolor: "background.paper",
          color: "text.primary",
          font: "inherit",
          fontWeight: 600,
          fontSize: "inherit",
          lineHeight: 1,
          verticalAlign: "middle",
          cursor: canOpenMenu ? "pointer" : "default",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          "&:hover": canOpenMenu
            ? {
                bgcolor: "action.hover",
                borderColor: "text.disabled",
              }
            : undefined,
        }}
      >
        <PodcastListInPill podcasts={pillPodcasts} />
        {canOpenMenu ? (
          <KeyboardArrowDown
            sx={{ fontSize: 18, opacity: 0.65, flexShrink: 0, ml: 0.25 }}
          />
        ) : null}
      </Box>

      {canOpenMenu && (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{
            paper: {
              sx: {
                width: "min(320px, calc(100vw - 32px))",
                maxWidth: "min(320px, calc(100vw - 32px))",
                mt: 0.5,
                overflow: "hidden",
                borderRadius: 2,
              },
            },
            list: {
              sx: { py: 1, px: 1, display: "flex", flexDirection: "column", gap: 0.5 },
            },
          }}
        >
          <MenuItem
            disabled
            disableRipple
            sx={{
              opacity: 1,
              py: 0,
              px: 0.75,
              minHeight: 36,
              cursor: "default",
              borderRadius: 0,
              bgcolor: "transparent",
              "&.Mui-disabled": { opacity: 1 },
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              Podcasts de la empresa
            </Typography>
          </MenuItem>
          {podcasts.map((podcast) => {
            const isTarget = podcast.podcastId === targetPodcastId;
            const isSelected = selectedPodcastIds.includes(podcast.podcastId);

            return (
              <MenuItem
                key={podcast.podcastId}
                disabled={isTarget}
                onClick={() => togglePodcast(podcast.podcastId)}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  borderRadius: 1.5,
                  py: 1,
                  px: 1.25,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.72,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <PodAvatarInline podcast={podcast} size={28} />
                  <Typography
                    variant="body2"
                    noWrap
                    title={podcast.title}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {podcast.title}
                  </Typography>
                  <Box
                    sx={{
                      width: 20,
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {isSelected ? (
                      <Check
                        sx={{
                          fontSize: 18,
                          opacity: isTarget ? 0.45 : 0.9,
                        }}
                      />
                    ) : null}
                  </Box>
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Typography>
  );
};

export default InviteMetaPodcastSubtitle;
